import { useEffect, useRef, useState } from "react";

import { Colors, Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import classNames from "classnames";
import { animate } from "motion";

import { Box, Flex } from "@/components/layout/flexbox";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { LIGHT_STEEL_GRAY } from "@/css/constants";

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  backgroundColor?: string;
  p?: number;
}

export function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
  backgroundColor = LIGHT_STEEL_GRAY,
  p = 12,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bodyRef.current) {
      const contentHeight = bodyRef.current.scrollHeight;

      if (isOpen) {
        animate(
          bodyRef.current,
          {
            height: contentHeight,
            opacity: 1,
          },
          {
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1],
          },
        );
      } else {
        animate(
          bodyRef.current,
          {
            height: 0,
            opacity: 0,
          },
          {
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1],
          },
        );
      }
    }
  }, [isOpen, children]);
  return (
    <StyledSection className={classNames({ "item-open": isOpen })}>
      <Clickable onClick={() => setIsOpen(!isOpen)}>
        <Flex
          py={2}
          justifyContent="space-between"
          alignItems="center"
          fontWeight={600}
        >
          {title}
          <div className="caret-container">
            <Icon icon="chevron-down" color={Colors.GRAY1} />
          </div>
        </Flex>
      </Clickable>
      <Box className="collapsible-item-content" ref={bodyRef}>
        <Flex
          p={p}
          backgroundColor={backgroundColor}
          className={"round-corners"}
          flexDirection="column"
        >
          {children}
        </Flex>
      </Box>
    </StyledSection>
  );
}

const StyledSection = styled.div`
  .collapsible-item-content {
    height: 0;
    opacity: 0;
    will-change: height;
    overflow: hidden;
  }

  .caret-container {
    transition: transform 0.3s ease;
  }

  &.item-open {
    .caret-container {
      transform: rotate(-180deg);
    }
  }
`;
